import React from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../../features/track';
import { RecurringTransactionConfigFragment } from '../../../graphql/fragments/recurringTransactionConfig.generated';
import { LegacyTransferType } from '../../../graphql/schema';
import { DAY_OF_WEEK_OPTIONS, MONTHLY_OPTIONS, frequencyCopy } from '../../models/automatic-transaction';
import { urlBuilder } from '../../urlBuilder';

type Props = {
  config: RecurringTransactionConfigFragment;
  frequency?: string | null;
  isWithdrawal: (sourceLegacySubAccountId: number) => boolean;
  isTransactionCardUpdatesEnabled?: boolean;
};

export const configFrequencyCopy = ({ frequency }: RecurringTransactionConfigFragment) => {
  switch (frequency.__typename) {
    case 'RecurringTransactionFrequencyAnnually':
      return frequencyCopy({
        frequency: 'Annually',
        month: MONTHLY_OPTIONS[frequency.month].value,
        dayOfMonth: frequency.day,
      });
    case 'RecurringTransactionFrequencyMonthly':
      return frequencyCopy({
        frequency: 'Monthly',
        monthlySelection1: frequency.day,
      });
    case 'RecurringTransactionFrequencyTwiceAMonth':
      return frequencyCopy({
        frequency: 'Twice a month',
        monthlySelection1: frequency.firstDay,
        monthlySelection2: frequency.secondDay,
      });
    case 'RecurringTransactionFrequencyWeekly':
      return frequencyCopy({
        frequency: 'Weekly',
        dayOfWeekSelection: DAY_OF_WEEK_OPTIONS[frequency.day - 1].value,
      });
    case 'RecurringTransactionFrequencyEveryOtherWeek':
      return frequencyCopy({
        frequency: 'Every other week',
        dayOfWeekSelection: DAY_OF_WEEK_OPTIONS[frequency.day - 1].value,
      });
    default:
      return '';
  }
};

export const RecurringTransactionsConfig: React.FC<Props> = ({
  config,
  frequency,
  isWithdrawal,
  isTransactionCardUpdatesEnabled = false,
}) => {
  let legacySubAccountId;
  if (config.source.__typename === 'CashAccount' || config.source.__typename === 'ManagedSubAccount') {
    legacySubAccountId = config.source.legacySubAccountId;
  }

  let isMoneyOutflow = false;
  if (legacySubAccountId) {
    isMoneyOutflow = isWithdrawal(legacySubAccountId);
  }

  const iconName = isMoneyOutflow ? 'ArrowCircleMinus' : 'ArrowCirclePlus';
  const transactionDirection = isMoneyOutflow ? `to ${config.destinationName}` : `from ${config.sourceName}`;

  let url;
  let intent;
  const configId = String(config.legacyId || config.legacyUuid || '');
  if (configId !== '') {
    switch (config.legacyTransferType) {
      case LegacyTransferType.Deposit:
        url = urlBuilder.appAutomaticDepositConfigEdit.absolute({ automaticDepositConfigId: configId });
        intent = 'Deposit';
        break;
      case LegacyTransferType.Withdrawal:
        url = urlBuilder.appAutomaticWithdrawalConfigEdit.absolute({ automaticWithdrawalConfigId: configId });
        intent = 'Withdrawal';
        break;
      case LegacyTransferType.Transfer:
        url = urlBuilder.appRecurringTransferConfigEdit.absolute({ recurringTransferConfigId: configId });
        intent = 'Transfer';
        break;
      default:
        break;
    }
  }
  const currentPath = encodeURIComponent(window.location.pathname);
  const returnTo = `return_to=${currentPath}`;
  const editUrl = url ? `${url}?${returnTo}` : undefined;

  return (
    <>
      {!isTransactionCardUpdatesEnabled && <Separator css={{ fullWidthWithInset: 'space.300' }} />}
      <ListRow key={config.id}>
        <ListRow.LeadingAsset>
          <Icon name={iconName} size="small" />
        </ListRow.LeadingAsset>
        <ListRow.LeadingContent>
          <Text>{`${formatMoney(config.amount)} ${transactionDirection}`}</Text>
          <Text variant="text50" color="text.weak">
            {frequency}
          </Text>
        </ListRow.LeadingContent>
        {editUrl && intent && config.isEditable && (
          <ListRow.TrailingContent>
            <SambaAutoLink to={editUrl} onClick={() => trackClick({ name: `Auto${intent}Intended` })}>
              Edit
            </SambaAutoLink>
          </ListRow.TrailingContent>
        )}
      </ListRow>
    </>
  );
};
